/**
 * TRACK A GAME (DESIGN-CHANGELOG 2026-09-03; `design/GT Mine.dc.html`, the
 * `trkShow` block). AA phase 2.
 *
 * 560px, fixed height so the list scrolls inside it. A Search Box over STEAM
 * (Marcus, 2026-09-04 — the prototype searched the catalog; `trackModalVM` on
 * how the two populations merge), a one-line state under it (`SEARCHING
 * STEAM…` / `N FROM STEAM` / offline), and the note says whose list this
 * feeds (`YOUR BACKLOG, NOT THE PARTY'S LIST`). `TRACK ▸` per row, `✓ ON IT`
 * once shelved, and the modal STAYS OPEN for a second pick — ✕ closes.
 */

import { CoverArt } from '../../components/CoverArt.tsx';
import { SearchBox } from '../../components/SearchBox.tsx';
import { useArt, useQueueArt } from '../../components/useArt.ts';
import { useQd } from '../../store/index.ts';
import { trackModalVM } from '../../store/selectors.ts';
import { Modal } from '../components/Modal.tsx';

const MONO = 'var(--gt-font-mono)';

type TrackRow = ReturnType<typeof trackModalVM>['rows'][number];

function Row({ g }: { g: TrackRow }): JSX.Element {
  const soloAdd = useQd((s) => s.soloAdd);
  const art = useArt(g.id + 'w', g.hdrCands);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 18px', borderBottom: '1px solid var(--gt-line-0)' }}>
      <div
        style={{
          position: 'relative',
          width: 64,
          height: 30,
          flex: 'none',
          background: g.fall,
          overflow: 'hidden',
          border: '1px solid var(--gt-line)',
        }}
      >
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={7} ini={g.ini} hasCover={art.hasCover} cover={art.cover} />
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span
          style={{
            fontFamily: 'var(--gt-font-sans)',
            fontWeight: 600,
            fontSize: 12.5,
            color: 'var(--gt-text)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {g.n}
        </span>
        <span
          style={{
            fontFamily: MONO,
            fontSize: 8.5,
            letterSpacing: '.06em',
            color: 'var(--gt-faint)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {g.meta}
        </span>
      </div>
      {g.has ? (
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: 'var(--acc)', padding: '7px 0', flex: 'none' }}>✓ ON IT</span>
      ) : (
        <span
          onClick={() => void soloAdd(g.id)}
          style={{
            fontFamily: MONO,
            fontSize: 9,
            letterSpacing: '.07em',
            color: 'var(--acc)',
            border: '1px solid var(--acc)',
            background: 'var(--accSoft)',
            padding: '7px 10px',
            cursor: 'pointer',
            flex: 'none',
          }}
        >
          TRACK ▸
        </span>
      )}
    </div>
  );
}

export default function TrackModal(): JSX.Element {
  const s = useQd();
  const v = trackModalVM(s);

  // Same fetch the phone sheet runs — see the note there.
  useQueueArt(v.rows.map((r) => r.id));

  return (
    <Modal
      onClose={() => s.closeTrack()}
      width={560}
      label="Track a game modal"
      header={{ title: 'TRACK A GAME', titleClr: 'var(--gt-text)', titleFs: '10px', note: "YOUR BACKLOG, NOT THE PARTY'S LIST" }}
      panelStyle={{ height: 'min(660px, 92vh)' }}
    >
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--gt-line)' }}>
        <SearchBox
          value={v.q}
          onChange={(e) => s.setTrkQ(e.target.value)}
          onClear={() => s.setTrkQ('')}
          placeholder="SEARCH STEAM…"
          w="100%"
          pfs={11}
          ifs={10.5}
          pad="8px 10px"
        />
        {v.hasNote ? (
          <div style={{ marginTop: 8, fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: v.busy ? 'var(--gt-muted)' : 'var(--gt-faint)' }}>
            {v.note}
          </div>
        ) : null}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {v.rows.map((g) => (
          <Row key={g.key} g={g} />
        ))}
        {v.empty ? (
          <div style={{ padding: '26px 18px', fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--gt-muted)' }}>
            {v.emptyTxt}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
